import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePwaInstallContext } from '../context/PwaInstallContext';
import { useSiteSettings } from '../SiteSettingsContext';
import {
  dismissPwaInstallBanner,
  wasPwaInstallBannerDismissed,
} from '../pwa/pwaInstallStorage';
import './pwa-install.css';

/** One-time mobile install hint above the WhatsApp FAB — dismissed via localStorage. */
export default function PwaInstallBanner() {
  const { pathname } = useLocation();
  const { canShowInstallOffer, runInstallAction } = usePwaInstallContext();
  const { settings } = useSiteSettings();
  const [bannerDismissed, setBannerDismissed] = useState(() => wasPwaInstallBannerDismissed());
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      !canShowInstallOffer ||
      bannerDismissed ||
      pathname.startsWith('/admin') ||
      pathname.startsWith('/checkout')
    ) {
      setVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setVisible(true), 2800);
    return () => window.clearTimeout(timer);
  }, [bannerDismissed, canShowInstallOffer, pathname]);

  if (!visible) return null;

  const rawHours = settings?.pwa_install_dismiss_hours;
  const hours = Number.isFinite(Number(rawHours)) ? Number(rawHours) : 1;
  const clampedHours = Math.min(168, Math.max(0, Math.round(hours)));
  const dismissForMs = clampedHours * 60 * 60 * 1000;

  const handleDismiss = () => {
    dismissPwaInstallBanner(dismissForMs);
    setBannerDismissed(true);
    setVisible(false);
  };

  const handleShowHow = () => {
    runInstallAction();
    handleDismiss();
  };

  return (
    <aside
      className="pwa-install-banner d-lg-none"
      role="dialog"
      aria-labelledby="pwa-install-banner-title"
      aria-describedby="pwa-install-banner-desc"
    >
      <button
        type="button"
        className="pwa-install-banner__close"
        aria-label="Dismiss"
        onClick={handleDismiss}
      >
        ×
      </button>
      <p id="pwa-install-banner-title" className="pwa-install-banner__title">
        Get the app on your home screen
      </p>
      <p id="pwa-install-banner-desc" className="pwa-install-banner__desc">
        Quick access to shop, track orders, and new collections.
      </p>
      <div className="pwa-install-banner__actions">
        <button type="button" className="btn btn-wine btn-sm" onClick={handleShowHow}>
          Show me how
        </button>
        <button type="button" className="btn btn-link btn-sm pwa-install-banner__later" onClick={handleDismiss}>
          Not now
        </button>
      </div>
    </aside>
  );
}
